const PUSH_ENABLED_KEY = "payinapo:push-enabled";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function getPushPermission() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission;
}

export function isPushEnabledLocally() {
  try {
    return localStorage.getItem(PUSH_ENABLED_KEY) === "1";
  } catch {
    return false;
  }
}

export function setPushEnabledLocally(enabled) {
  try {
    if (enabled) {
      localStorage.setItem(PUSH_ENABLED_KEY, "1");
    } else {
      localStorage.removeItem(PUSH_ENABLED_KEY);
    }
  } catch {
    /* ignore */
  }
}

export async function getServiceWorkerRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  return navigator.serviceWorker.ready;
}

export async function subscribeToPushNotifications() {
  const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  if (!vapidPublicKey) {
    throw new Error("Missing REACT_APP_VAPID_PUBLIC_KEY");
  }

  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    throw new Error("Service worker is not ready");
  }

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  }

  return subscription;
}

export async function unsubscribeFromPushNotifications() {
  const registration = await getServiceWorkerRegistration();
  if (!registration) return;

  const subscription = await registration.pushManager.getSubscription();
  if (subscription) {
    await subscription.unsubscribe();
  }
}

export async function requestPushPermission() {
  if (!isPushSupported()) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  return Notification.requestPermission();
}

export async function enablePushNotifications(userId, upsertFn) {
  const permission = await requestPushPermission();
  if (permission !== "granted") {
    setPushEnabledLocally(false);
    return { ok: false, permission };
  }

  const subscription = await subscribeToPushNotifications();
  await upsertFn(userId, subscription, navigator.userAgent);
  setPushEnabledLocally(true);
  return { ok: true, permission: "granted" };
}

export async function disablePushNotifications(userId, removeFn) {
  const registration = await getServiceWorkerRegistration();
  const subscription = registration
    ? await registration.pushManager.getSubscription()
    : null;

  if (subscription) {
    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    if (userId && removeFn) {
      await removeFn(userId, endpoint);
    }
  }

  setPushEnabledLocally(false);
  return { ok: true };
}
